import {AttrsSchema, Component} from 'panel';
import ResizeObserver from 'resize-observer-polyfill';

import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {panel2react} from 'common/util/react';

import {NarrowMode, SelectEvent, ToggleJsxProps, ToggleOption, ToggleSize, ToggleState, ToggleTheme} from './types';
import css from './index.inline.styl';
import {template} from './template';

export class Toggle extends Component<ToggleState, ToggleJsxProps['attrs']> {
  pendingHoverEvent: NodeJS.Timeout = null;
  widthResizeObserver: ResizeObserver;

  static get attrsSchema(): AttrsSchema<ToggleJsxProps['attrs']> {
    return {
      ...super.attrsSchema,
      selected: `string`,
      size: {type: `string`, enum: Object.values(ToggleSize), default: ToggleSize.Medium},
      theme: {
        type: `string`,
        enum: Object.values(ToggleTheme),
        default: ToggleTheme.SegmentedController,
      },
      options: {type: `json`, description: `toggle options`, tsType: `ToggleOption[]`},
      'icon-only-width-threshold': {type: `number`, description: `Threshold at which labels are hidden, in pixels`},
    };
  }
  connectedCallback(): void {
    this.setNarrowMode(this.parentElement.getBoundingClientRect().width);
    super.connectedCallback();
  }

  disconnectedCallback(): void {
    this.widthResizeObserver?.disconnect();
  }

  get config() {
    return {
      css,
      template,
      useShadowDom: true,
      defaultState: {
        narrowMode: null,
      },

      helpers: {
        selectOption: (value) => {
          this.selectOption(value);
        },

        // NOTE: querying slotted children for options is a deprecated method. Pass options via json attr.
        // attr changes always result in an update -> render.
        // if something changes the slotted children, the tab won't re-render causing all sorts of ui glitches.
        toggleOptions: (): ToggleOption[] =>
          this.attr(`options`)
            ? this.attr(`options`)
            : Array.from(this.children)
                .filter((el) => el.tagName.toLowerCase() === `mp-toggle-option`)
                .map((el) => {
                  const icon = el.getAttribute(`icon`);
                  return {
                    disabled: this.isAttributeEnabled.call(el, `disabled`),
                    label: el.textContent,
                    value: el.getAttribute(`value`),
                    ...(icon ? {icon} : {}),
                  };
                }),
      },
    };
  }

  get value() {
    return this.attr(`selected`);
  }

  selectOption(value: string) {
    if (this.value !== value) {
      this.dispatchEvent(new CustomEvent(`select`, {detail: {selected: value}}) as SelectEvent);
    }
  }

  hovered(value: string) {
    if (this.value !== value) {
      this.pendingHoverEvent = setTimeout(() => {
        this.dispatchEvent(new CustomEvent(`hovered`, {detail: {hovered: value}}));
        this.pendingHoverEvent = null;
      }, 100);
    }
  }

  cancelHover() {
    if (this.pendingHoverEvent) {
      clearTimeout(this.pendingHoverEvent);
      this.pendingHoverEvent = null;
    }
  }

  wrapperInserted(vnode) {
    this.widthResizeObserver?.disconnect();
    if (this.attr(`icon-only-width-threshold`)) {
      this.widthResizeObserver = new ResizeObserver((entries) => {
        const {width} = entries[0].contentRect;
        this.setNarrowMode(width);
      });
      this.widthResizeObserver.observe(vnode.elm);
    }
  }

  private setNarrowMode(width: number) {
    const onlyIconsWidth = this.attr(`icon-only-width-threshold`);
    if (!onlyIconsWidth) {
      return;
    }

    const narrowMode = width <= onlyIconsWidth ? NarrowMode.OnlyIcons : null;
    if (narrowMode !== this.state.narrowMode) {
      this.update({narrowMode});
    }
  }
}

export const ToggleEl = defineAndMakeJsxFactory<ToggleJsxProps>(`mp-toggle`, Toggle);
export const ToggleReact = panel2react<ToggleJsxProps>(`mp-toggle`, Toggle);
